import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams, NavLink, Outlet } from 'react-router-dom';
import { wikiApi } from '../../api/wiki.js';

/**
 * Two-panel space layout: page tree on the left, page content on the right.
 * The right side renders <Outlet/> so /wiki/:spaceKey/:pageSlug and
 * /wiki/:spaceKey/:pageSlug/edit can swap views without losing the tree.
 */
export default function SpaceLayout() {
  const { spaceKey } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('');
  const [creating, setCreating] = useState(null); // { parent_id }

  const reload = () => wikiApi.getSpace(spaceKey).then(setData).catch(() => setData(null));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, [spaceKey]);

  const filteredTree = useMemo(() => {
    if (!data?.tree) return [];
    if (!filter) return data.tree;
    const lc = filter.toLowerCase();
    function walk(nodes) {
      return nodes
        .map((n) => ({ ...n, children: walk(n.children || []) }))
        .filter((n) => n.title.toLowerCase().includes(lc) || n.children.length > 0);
    }
    return walk(data.tree);
  }, [data, filter]);

  if (!data) return <div className="muted" style={{ padding: 24 }}>Loading space…</div>;

  return (
    <div data-testid="wiki-space" data-space-key={spaceKey} style={{ display: 'grid', gridTemplateColumns: '260px 1fr', minHeight: 'calc(100vh - var(--app-header-h))' }}>
      {/* Sidebar */}
      <aside style={{ borderRight: '1px solid #e2e8f0', background: '#f8fafc', padding: 12, overflowY: 'auto' }}>
        <header style={{ padding: '4px 8px 12px', display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 22 }}>{data.space.icon}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>{data.space.name}</div>
            <div style={{ fontSize: 11, color: '#64748b' }}>{data.space.key}</div>
          </div>
        </header>
        <button
          className="btn ghost small"
          style={{ width: '100%', justifyContent: 'flex-start' }}
          data-testid="create-page-btn"
          onClick={() => setCreating({ parent_id: null })}
        >
          + Create page
        </button>
        <input
          type="search"
          placeholder="Filter pages…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{ width: '100%', marginTop: 8, marginBottom: 8 }}
          aria-label="Filter pages"
        />
        <PageTree
          nodes={filteredTree}
          spaceKey={spaceKey}
          onAddChild={(parentId) => setCreating({ parent_id: parentId })}
          onMoved={reload}
        />
      </aside>

      {/* Content panel */}
      <main style={{ overflowY: 'auto' }}>
        <Outlet context={{ space: data.space, reload }} />
      </main>

      {creating && (
        <CreatePageModal
          spaceKey={spaceKey}
          parentId={creating.parent_id}
          onClose={() => setCreating(null)}
          onCreated={(p) => {
            setCreating(null);
            reload();
            navigate(`/wiki/${spaceKey}/${p.slug}/edit`);
          }}
        />
      )}
    </div>
  );
}

function PageTree({ nodes, spaceKey, onAddChild, onMoved, depth = 0 }) {
  return (
    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
      {nodes.map((n) => (
        <PageNode key={n.id} node={n} spaceKey={spaceKey} depth={depth} onAddChild={onAddChild} onMoved={onMoved} />
      ))}
    </ul>
  );
}

function PageNode({ node, spaceKey, depth, onAddChild, onMoved }) {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = (node.children || []).length > 0;
  const isDraft = node.status === 'draft';
  return (
    <li data-testid="page-tree-row" data-page-id={node.id}>
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 4,
          padding: '4px 6px', paddingLeft: depth * 14 + 6, borderRadius: 4,
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = '#eef2ff'}
        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            aria-label={expanded ? 'Collapse' : 'Expand'}
            style={{ background: 'transparent', border: 'none', padding: 0, width: 16, cursor: 'pointer', color: '#64748b' }}
          >
            {expanded ? '▾' : '▸'}
          </button>
        ) : (
          <span style={{ width: 16 }} />
        )}
        <NavLink
          to={`/wiki/${spaceKey}/${node.slug}`}
          style={({ isActive }) => ({
            flex: 1, minWidth: 0,
            padding: '2px 4px', borderRadius: 3,
            color: isActive ? '#1e40af' : '#0f172a',
            background: isActive ? '#dbeafe' : 'transparent',
            fontStyle: isDraft ? 'italic' : 'normal',
            fontWeight: isActive ? 600 : 500,
            fontSize: 13,
            textDecoration: 'none',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          })}
        >
          {node.emoji_icon ? <span style={{ marginRight: 4 }}>{node.emoji_icon}</span> : null}
          {node.title}
          {isDraft && <span style={{ color: '#94a3b8', marginLeft: 4 }}>(draft)</span>}
        </NavLink>
        <button
          type="button"
          onClick={() => onAddChild(node.id)}
          aria-label={`Add child page under ${node.title}`}
          title="Add child page"
          style={{ background: 'transparent', border: 'none', padding: 2, cursor: 'pointer', color: '#94a3b8', fontSize: 14 }}
        >
          +
        </button>
      </div>
      {hasChildren && expanded && (
        <PageTree nodes={node.children} spaceKey={spaceKey} depth={depth + 1} onAddChild={onAddChild} onMoved={onMoved} />
      )}
    </li>
  );
}

function CreatePageModal({ spaceKey, parentId, onClose, onCreated }) {
  const [title, setTitle] = useState('');
  const [emoji, setEmoji] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const out = await wikiApi.createPage(spaceKey, {
        title,
        parent_id: parentId,
        emoji_icon: emoji || null,
        status: 'draft',
      });
      onCreated(out.page);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create page');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div role="dialog" aria-modal="true" aria-label="Create wiki page"
         style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
         onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
            className="card" style={{ width: 'min(90vw, 480px)', padding: 20 }}
            data-testid="create-page-modal">
        <h2 style={{ marginTop: 0 }}>Create page</h2>
        <label>Title<input value={title} onChange={(e) => setTitle(e.target.value)} required autoFocus data-testid="new-page-title" /></label>
        <label>Emoji (optional)<input value={emoji} onChange={(e) => setEmoji(e.target.value)} maxLength={4} style={{ width: 100 }} /></label>
        {error && <div className="alert error" role="alert">{error}</div>}
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 12 }}>
          <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn primary" disabled={submitting || !title}>
            {submitting ? 'Creating…' : 'Create page'}
          </button>
        </div>
      </form>
    </div>
  );
}
